export const API_BASE_URL = "";

export function getFullUrl(url: string) {
  if (typeof window === 'undefined' && url.startsWith('/')) {
    const backendHost = process.env.BACKEND_URL || 'http://127.0.0.1:8000';
    return `${backendHost}${url}`;
  }
  return url;
}

export function getCurrentUser() {
  if (typeof window === "undefined") return null;
  const userStr = localStorage.getItem("user") || localStorage.getItem("prop_user");
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

export async function logoutUser() {
  try {
    await fetch(getFullUrl(`${API_BASE_URL}/api/auth/logout`), {
      method: "POST",
      credentials: "include",
    });
  } catch {}
  if (typeof window !== "undefined") {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("prop_user");
    document.cookie = "token=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = "access_token=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
}

// Fetch helper that automatically includes cookies and Bearer token
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  if (!token) {
    throw new Error("شما لاگین نیستید");
  }
  const headers = new Headers(options.headers || {});
  headers.set("ngrok-skip-browser-warning", "69420");
  headers.set("Authorization", `Bearer ${token}`);
  
  const finalOptions = {
    ...options,
    headers,
    credentials: "include" as RequestCredentials,
  };
  try {
    return await fetch(getFullUrl(url), finalOptions);
  } catch (err: any) {
    if (err.message === "شما لاگین نیستید") throw err;
    console.warn("Network fetch error:", err.message);
    throw new Error("خطا در برقراری ارتباط با سرور (Network Error)");
  }
}

// ۱. دریافت لیست پلن‌های چالش
export async function fetchPlans() {
  const res = await fetch(getFullUrl(`${API_BASE_URL}/api/plans`));
  if (!res.ok) throw new Error("خطا در دریافت پلن‌ها");
  return res.json();
}

// ۲. دریافت اطلاعات کامل داشبورد کاربر (اکانت‌های متاتریدر، تراکنش‌ها و تیکت‌ها)
export async function fetchUserDashboard() {
  // no longer passing userId, it relies on JWT
  const res = await fetchWithAuth(`${API_BASE_URL}/api/user/dashboard`);
  if (!res.ok) {
     if (res.status === 401) throw new Error("شما لاگین نیستید");
     throw new Error("خطا در دریافت اطلاعات داشبورد");
  }
  return res.json();
}

// دریافت اطلاعات چارت اکوئیتی و بالانس اکانت
export async function fetchAccountChartData(accountId: number, timeframe: string = "7d") {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/user/accounts/${accountId}/chart-data?timeframe=${timeframe}`);
  if (!res.ok) throw new Error("خطا در دریافت اطلاعات نمودار");
  return res.json();
}

// ۳. درخواست خرید چالش جدید (هدایت به درگاه)
export async function buyChallengePlan(planId: number, paymentMethod: string = "USDT", discountCode?: string) {
  const bodyData: any = { plan_id: planId, payment_method: paymentMethod };
  if (discountCode) {
    bodyData.discount_code = discountCode;
  }
  const res = await fetchWithAuth(`${API_BASE_URL}/api/payments/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(bodyData),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در ثبت درخواست پرداخت");
  return data; // contains payment_url
}

// ارسال درخواست برداشت سود
export async function requestPayout(accountId: number, amount: number, walletAddress: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/user/payout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ account_id: accountId, amount, wallet_address: walletAddress }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در ثبت درخواست برداشت");
  return data;
}

// ثبت درخواست ارتقای حساب چالش (به فاز ۲ یا فاندد)
export async function requestAccountUpgrade(accountId: number, note?: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/user/accounts/${accountId}/upgrade-request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در ثبت درخواست ارتقا");
  return data;
}

export async function requestDeposit(amount: number, paymentMethod: string, txHash?: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/user/wallet/deposit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amount, payment_method: paymentMethod, tx_hash: txHash }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در ثبت درخواست واریز");
  return data;
}

// ایجاد لینک درگاه پرداخت ارز دیجیتال NOWPayments
export async function createNowpaymentsDeposit(amount: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/payments/nowpayments/create-deposit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amount }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در اتصال به درگاه پرداخت NOWPayments");
  return data;
}

// تایید خودکار و شارژ آنی واریز درگاه
export async function verifyNowpaymentsDeposit(params: { deposit_id?: number; order_id?: string }) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/payments/nowpayments/verify-deposit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در تایید تراکنش درگاه");
  return data;
}

// لغو فاکتور پرداخت‌نشده توسط کاربر
export async function cancelNowpaymentsDeposit(params: { deposit_id: number }) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/payments/nowpayments/cancel-deposit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در لغو فاکتور");
  return data;
}



// ۴. ارسال تیکت پشتیبانی
export async function sendSupportTicket(subject: string, message: string) {
  // no longer passing userId in body
  const res = await fetchWithAuth(`${API_BASE_URL}/api/tickets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subject, message }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در ارسال تیکت");
  return data;
}

// ۵. دریافت تیکت‌های یک کاربر
export async function fetchUserTickets() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/tickets`);
  if (!res.ok) throw new Error("خطا در دریافت تیکت‌ها");
  return res.json();
}

export async function replyUserTicket(ticketId: number, message: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/tickets/${ticketId}/reply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در ارسال پاسخ");
  return data;
}

export async function closeUserTicket(ticketId: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/tickets/${ticketId}/close`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در بستن تیکت");
  return data;
}

// ۶. دریافت کل اطلاعات سایت برای پنل ادمین
export async function fetchAdminData() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/overview`);
  if (!res.ok) {
     if (res.status === 403) throw new Error("دسترسی غیرمجاز");
     throw new Error("خطا در دریافت اطلاعات ادمین");
  }
  return res.json();
}
// ۷. ارسال مدارک احراز هویت (KYC)
export async function uploadKYCDocuments(frontImage: File, backImage: File, selfieImage: File) {
  const formData = new FormData();
  formData.append("front_image", frontImage);
  formData.append("back_image", backImage);
  formData.append("selfie_image", selfieImage);

  const res = await fetchWithAuth(`${API_BASE_URL}/api/kyc/upload`, {
    method: "POST",
    body: formData,
  });
  
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در ارسال مدارک هویتی");
  return data;
}

// ۸. بررسی مدارک توسط ادمین
export async function reviewKYC(userId: number, status: "Approved" | "Rejected", rejectionReason?: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/kyc/${userId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, rejection_reason: rejectionReason }),
  });
  
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "خطا در ثبت وضعیت KYC");
  return data;
}
// ۹. دریافت اطلاعات بازاریابی (Affiliate)
export async function fetchAffiliateStats() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/affiliate/stats`);
  if (!res.ok) {
     if (res.status === 401) throw new Error("شما لاگین نیستید");
     throw new Error("خطا در دریافت آمار افیلیت");
  }
  return res.json();
}

// ۱۰. دریافت لیدربرد (برترین تریدرها)
export async function fetchLeaderboard() {
  const res = await fetch(getFullUrl(`${API_BASE_URL}/api/leaderboard`));
  if (!res.ok) throw new Error("خطا در دریافت لیست لیدربرد");
  return res.json();
}
// ۱۱. دریافت اعلان‌های کاربر (Notifications)
export async function fetchNotifications() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications`);
  if (!res.ok) {
     if (res.status === 401) throw new Error("شما لاگین نیستید");
     throw new Error("خطا در دریافت اعلان‌ها");
  }
  return res.json();
}

// ۱۲. مارک کردن اعلان به عنوان خوانده شده
export async function markNotificationAsRead(notificationId: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/notifications/${notificationId}/read`, {
    method: "PUT"
  });
  if (!res.ok) throw new Error("خطا در خوانده شدن اعلان");
  return res.json();
}
export async function fetchPendingKYC() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/kyc/pending`);
  if (!res.ok) {
     if (res.status === 401 || res.status === 403) throw new Error("دسترسی غیرمجاز");
     throw new Error("خطا در دریافت لیست احراز هویت");
  }
  return res.json();
}

export async function fetchAdminDeposits() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/deposits`);
  if (!res.ok) throw new Error("خطا در دریافت لیست تراکنش‌های واریز");
  return res.json();
}

export async function reviewAdminDeposit(depositId: number, status: string, admin_note?: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/deposits/${depositId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, admin_note }),
  });
  if (!res.ok) throw new Error("خطا در بررسی تراکنش واریز");
  return res.json();
}

export async function fetchAdminTickets() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/tickets`);
  if (!res.ok) throw new Error("خطا در دریافت لیست تیکت‌ها");
  return res.json();
}

export async function fetchAdminTicketDetails(ticketId: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/tickets/${ticketId}`);
  if (!res.ok) throw new Error("خطا در دریافت جزئیات تیکت");
  return res.json();
}

export async function replyAdminTicket(ticketId: number, reply: string, status: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/tickets/${ticketId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reply, status }),
  });
  if (!res.ok) throw new Error("خطا در ثبت پاسخ تیکت");
  return res.json();
}

export async function createAdminPlan(data: any) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/plans`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("خطا در ایجاد پلن");
  return res.json();
}

export async function updateAdminPlan(planId: number, data: any) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/plans/${planId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("خطا در بروزرسانی پلن");
  return res.json();
}

export async function deleteAdminPlan(planId: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/plans/${planId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("خطا در حذف پلن");
  return res.json();
}

export async function adminGetUsers() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/users`);
  if (!res.ok) {
    const errorText = await res.text();
    console.error("Backend Error on adminGetUsers:", res.status, errorText);
    throw new Error(`Failed to fetch users (Status ${res.status}): ${errorText}`);
  }
  return res.json();
}

export async function adminUpdateUser(userId: number, data: any) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/users/${userId}`, {
    method: 'PUT',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error("Failed to update user");
  return res.json();
}

export async function adminGetPayouts() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/payouts`);
  if (!res.ok) throw new Error("Failed to fetch payouts");
  return res.json();
}

export async function reviewAdminPayout(payoutId: number, status: string, admin_note?: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/payouts/${payoutId}`, {
    method: 'PUT',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, admin_note })
  });
  if (!res.ok) throw new Error("Failed to update payout");
  return res.json();
}

export async function adminGetAccounts() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/accounts`);
  if (!res.ok) throw new Error("Failed to fetch accounts");
  return res.json();
}

export async function adminUpdateAccount(accountId: number, data: any) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/accounts/${accountId}`, {
    method: 'PUT',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error("Failed to update account");
  return res.json();
}

export async function adminApproveUpgrade(accountId: number, data?: { 
  new_phase?: string; 
  new_status?: string; 
  admin_note?: string;
  delivery_mode?: string;
  mt5_login?: number;
  mt5_password?: string;
  mt5_master_password?: string;
  mt5_server?: string;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/accounts/${accountId}/approve-upgrade`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data || {})
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "خطا در تایید ارتقای حساب");
  return resData;
}

export async function adminRejectUpgrade(accountId: number, rejection_reason?: string) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/accounts/${accountId}/reject-upgrade`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "Rejected", rejection_reason: rejection_reason || "درخواست ارتقا رد شد." })
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "خطا در رد درخواست ارتقا");
  return resData;
}

export async function adminSendGiftAccount(data: {
  user_id: number;
  plan_id: number;
  phase?: string;
  delivery_mode?: string;
  mt5_login?: number;
  mt5_password?: string;
  mt5_master_password?: string;
  mt5_server?: string;
  gift_note?: string;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/accounts/gift`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "خطا در صدور اکانت هدیه");
  return resData;
}


// ---- Discount API ----
export async function fetchDiscounts() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/discounts`);
  if (!res.ok) throw new Error("Failed to fetch discounts");
  return res.json();
}

export async function createDiscount(data: { code: string, discount_percent: number, max_uses: number }) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/discounts`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to create discount");
  return resData;
}

export async function toggleDiscountStatus(discountId: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/discounts/${discountId}/toggle`, {
    method: 'PATCH'
  });
  if (!res.ok) throw new Error("Failed to toggle discount status");
  return res.json();
}

export async function deleteDiscount(discountId: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/discounts/${discountId}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error("Failed to delete discount");
  return res.json();
}

export async function validateDiscountCode(code: string) {
  const res = await fetch(`${API_BASE_URL}/api/payments/validate-discount`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Invalid discount code");
  return data; // { message, discount_percent }
}

// ---- FAQ API ----
export async function fetchPublicFaqs() {
  try {
    const res = await fetch(getFullUrl(`${API_BASE_URL}/api/faqs`), { cache: 'no-store' });
    if (!res.ok) throw new Error("Failed to fetch FAQs");
    return await res.json();
  } catch (err) {
    console.warn("fetchPublicFaqs fallback to defaults:", err);
    return null;
  }
}

export async function adminGetFaqs() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/faqs`);
  if (!res.ok) throw new Error("Failed to fetch admin FAQs");
  return res.json();
}

export async function adminCreateFaq(data: {
  question_fa: string;
  answer_fa: string;
  question_en?: string;
  answer_en?: string;
  category?: string;
  display_order?: number;
  is_active?: number;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/faqs`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to create FAQ");
  return resData;
}

export async function adminUpdateFaq(id: number, data: {
  question_fa?: string;
  answer_fa?: string;
  question_en?: string;
  answer_en?: string;
  category?: string;
  display_order?: number;
  is_active?: number;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/faqs/${id}`, {
    method: 'PUT',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to update FAQ");
  return resData;
}

export async function adminToggleFaqStatus(id: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/faqs/${id}/toggle`, {
    method: 'PATCH',
  });
  if (!res.ok) throw new Error("Failed to toggle FAQ status");
  return res.json();
}

export async function adminDeleteFaq(id: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/faqs/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error("Failed to delete FAQ");
  return res.json();
}

// ---- Education API ----
export async function fetchPublicEducations() {
  try {
    const res = await fetch(getFullUrl(`${API_BASE_URL}/api/education`), { cache: 'no-store' });
    if (!res.ok) throw new Error("Failed to fetch educations");
    return await res.json();
  } catch (err) {
    console.warn("fetchPublicEducations fallback to defaults:", err);
    return null;
  }
}

export async function adminGetEducations() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/education`);
  if (!res.ok) throw new Error("Failed to fetch admin educations");
  return res.json();
}

export async function adminCreateEducation(data: {
  title_fa: string;
  description_fa: string;
  content_fa?: string;
  title_en?: string;
  description_en?: string;
  content_en?: string;
  category?: string;
  level?: string;
  duration?: string;
  video_url?: string;
  display_order?: number;
  is_active?: number;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/education`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to create education");
  return resData;
}

export async function adminUpdateEducation(id: number, data: {
  title_fa?: string;
  description_fa?: string;
  content_fa?: string;
  title_en?: string;
  description_en?: string;
  content_en?: string;
  category?: string;
  level?: string;
  duration?: string;
  video_url?: string;
  display_order?: number;
  is_active?: number;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/education/${id}`, {
    method: 'PUT',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to update education");
  return resData;
}

export async function adminToggleEducationStatus(id: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/education/${id}/toggle`, {
    method: 'PATCH',
  });
  if (!res.ok) throw new Error("Failed to toggle education status");
  return res.json();
}

export async function adminDeleteEducation(id: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/education/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error("Failed to delete education");
  return res.json();
}

// ۱۳. دریافت چارت‌ها و گزارشات مالی پنل ادمین
export async function fetchAdminFinancialReports(timeframe: string = "30d") {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/financial-reports?timeframe=${timeframe}`);
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`خطا در دریافت گزارشات مالی: ${errorText}`);
  }
  return res.json();
}

// ---- Platform Rules API ----
export async function fetchPublicRules() {
  try {
    const res = await fetch(getFullUrl(`${API_BASE_URL}/api/rules`), { cache: 'no-store' });
    if (!res.ok) throw new Error("Failed to fetch rules");
    return await res.json();
  } catch (err) {
    console.warn("fetchPublicRules fallback:", err);
    return null;
  }
}

export async function adminGetRules() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/rules`);
  if (!res.ok) throw new Error("Failed to fetch admin rules");
  return res.json();
}

export async function adminCreateRule(data: {
  title_fa: string;
  desc_fa: string;
  title_en?: string;
  desc_en?: string;
  category?: string;
  display_order?: number;
  is_active?: number;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/rules`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to create rule");
  return resData;
}

export async function adminUpdateRule(id: number, data: {
  title_fa?: string;
  desc_fa?: string;
  title_en?: string;
  desc_en?: string;
  category?: string;
  display_order?: number;
  is_active?: number;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/rules/${id}`, {
    method: 'PUT',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to update rule");
  return resData;
}

export async function adminToggleRuleStatus(id: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/rules/${id}/toggle`, {
    method: 'PATCH',
  });
  if (!res.ok) throw new Error("Failed to toggle rule status");
  return res.json();
}

export async function adminDeleteRule(id: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/rules/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error("Failed to delete rule");
  return res.json();
}

// ---- Public Payouts API ----
export async function fetchPublicPayouts() {
  try {
    const res = await fetch(getFullUrl(`${API_BASE_URL}/api/payouts/public`), { cache: 'no-store' });
    if (!res.ok) throw new Error("Failed to fetch payouts");
    return await res.json();
  } catch (err) {
    console.warn("fetchPublicPayouts fallback:", err);
    return [];
  }
}

// ---- Admin Certificates API ----
export async function uploadCertificateImage(file: File): Promise<{ image_url: string; filename: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/certificates/upload`, {
    method: 'POST',
    body: formData,
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "خطا در آپلود تصویر گواهی");
  return resData;
}

export async function fetchAdminCertificates() {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/certificates`);
  if (!res.ok) throw new Error("Failed to fetch certificates");
  return res.json();
}

export async function createCertificate(data: {
  image_url?: string;
  trader_name?: string;
  account_size?: number;
  amount?: number;
  split_percent?: number;
  wallet_masked?: string;
  date_label?: string;
  certificate_no?: string;
  display_order?: number;
  is_active?: number;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/certificates`, {
    method: 'POST',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to create certificate");
  return resData;
}

export async function updateCertificate(id: number, data: {
  image_url?: string;
  trader_name?: string;
  account_size?: number;
  amount?: number;
  split_percent?: number;
  wallet_masked?: string;
  date_label?: string;
  certificate_no?: string;
  display_order?: number;
  is_active?: number;
}) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/certificates/${id}`, {
    method: 'PUT',
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });
  const resData = await res.json();
  if (!res.ok) throw new Error(resData.detail || "Failed to update certificate");
  return resData;
}

export async function toggleCertificateStatus(id: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/certificates/${id}/toggle`, {
    method: 'PATCH'
  });
  if (!res.ok) throw new Error("Failed to toggle certificate status");
  return res.json();
}

export async function deleteCertificate(id: number) {
  const res = await fetchWithAuth(`${API_BASE_URL}/api/admin/certificates/${id}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error("Failed to delete certificate");
  return res.json();
}




